import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api, qs } from "@/lib/api";
import { formatDateTime, formatInt } from "@/lib/format";
import { Empty, PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { useContactNames } from "./useContactNames";

interface LinkRow {
  contact_id: number;
  status: string;
  dry_run: boolean;
  expected_last4: string | null;
  expected_brand: string | null;
  detail: string | null;
  attempts: number;
  checked_at: string;
}

interface Report {
  counts: { status: string; dry_run: boolean; n: number }[];
  rows: LinkRow[];
  active_subscriptions_without_card: number[];
  message: string;
}

type Variant = "success" | "warning" | "destructive" | "muted" | "default";

/** billing_cim_links statuses, grouped the way Brandon acts on them. */
export const LINK_STATUS: Record<string, { label: string; variant: Variant; group: "ready" | "member" | "problem" | "waiting" }> = {
  linked: { label: "linked", variant: "success", group: "ready" },
  added_from_transaction: { label: "card added from last charge", variant: "success", group: "ready" },
  created_from_transaction: { label: "card saved from last charge", variant: "success", group: "ready" },
  no_profile: { label: "no saved card", variant: "warning", group: "member" },
  mismatch: { label: "saved card doesn't match", variant: "warning", group: "member" },
  expired: { label: "card expired", variant: "warning", group: "member" },
  failed: { label: "lookup failed", variant: "destructive", group: "problem" },
  skipped: { label: "skipped (card removed here)", variant: "muted", group: "problem" },
  pending: { label: "waiting to be checked", variant: "muted", group: "waiting" },
};

const GROUPS: { key: "ready" | "member" | "problem" | "waiting"; title: string; sub: string }[] = [
  { key: "ready", title: "Ready", sub: "renewals can charge this card" },
  { key: "member", title: "Needs the member", sub: "send an update-card link" },
  { key: "problem", title: "Problems", sub: "look at the detail" },
  { key: "waiting", title: "Waiting", sub: "the job hasn't reached them yet" },
];

const PAGE = 200;

/** Go-live: how far linking Keap-era saved cards has got (billing_cards_link_report), and which active subscriptions still have no card. */
export function CardLinksPage() {
  const [status, setStatus] = useState("");
  const [shown, setShown] = useState(PAGE);
  const q = useQuery({
    queryKey: ["card-link-report", status],
    queryFn: () => api<Report>("/api/v1/billing/cards/link-report" + qs({ status: status || undefined, limit: 3000 })),
    refetchInterval: 30_000,
  });
  const r = q.data;
  const rows = r?.rows ?? [];
  const visible = rows.slice(0, shown);
  const name = useContactNames(visible.map((x) => x.contact_id));
  const real = (r?.counts ?? []).filter((c) => !c.dry_run);
  const dry = (r?.counts ?? []).filter((c) => c.dry_run);
  const total = (list: Report["counts"], g: string) => list.filter((c) => LINK_STATUS[c.status]?.group === g).reduce((s, c) => s + c.n, 0);
  const missing = r?.active_subscriptions_without_card ?? [];
  return (
    <div className="mx-auto max-w-[1200px] space-y-3">
      <PageHeader title="Card links" sub="Go-live: connecting members to the cards Keap saved">
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Keap saved members' cards at Authorize.net. Before the CRM takes over renewals, a job matches each member to their saved card (or saves the card behind their
          latest charge). Claude runs it as a dry run first, then for real with Brandon's OK; no money moves. Only card brand and last four show here. Updates every 30
          seconds.
        </p>
      </PageHeader>
      {q.isLoading && <Skeleton className="h-40" />}
      {q.error && <p className="text-sm text-[var(--color-danger-fg)]">{q.error.message}</p>}
      {r && (
        <>
          <Card className="p-3">
            <h2 className="font-semibold">Active subscriptions with no usable card</h2>
            {missing.length === 0 ? (
              <p className="mt-1 text-sm text-[var(--color-success-fg)]">None. Every active subscription has a live card.</p>
            ) : (
              <>
                <p className="mt-1 text-sm">
                  <span className="num font-semibold text-[var(--color-warning-fg)]">{formatInt(missing.length)}</span> have no live card linked. Their next renewal fails
                  unless the member has another saved card or adds one (subscriptions still charged in Keap included).
                </p>
                <ul className="mt-2 flex max-h-40 flex-wrap gap-x-3 gap-y-1 overflow-y-auto text-sm">
                  {missing.map((id) => (
                    <li key={id}>
                      <Link to="/billing/subscriptions/$subscriptionId" params={{ subscriptionId: String(id) }} className="num text-[var(--color-primary)] hover:underline">
                        #{id}
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>
          {(r.counts ?? []).length === 0 ? (
            <Card>
              <Empty title="The link job hasn't run yet." />
            </Card>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {GROUPS.map((g) => (
                <Card key={g.key} className="p-3">
                  <div className="text-sm font-medium">{g.title}</div>
                  <div className="num text-2xl font-semibold">{formatInt(total(real, g.key))}</div>
                  <div className="text-xs text-[var(--color-muted-foreground)]">{g.sub}</div>
                  <ul className="mt-2 space-y-0.5 text-xs">
                    {Object.entries(LINK_STATUS)
                      .filter(([, s]) => s.group === g.key)
                      .map(([k, s]) => {
                        const n = real.filter((c) => c.status === k).reduce((sum, c) => sum + c.n, 0);
                        const d = dry.filter((c) => c.status === k).reduce((sum, c) => sum + c.n, 0);
                        return n || d ? (
                          <li key={k} className="flex justify-between gap-2">
                            <span>{s.label}</span>
                            <span className="num">
                              {formatInt(n)}
                              {d ? <span className="text-[var(--color-muted-foreground)]"> (+{formatInt(d)} dry run)</span> : null}
                            </span>
                          </li>
                        ) : null;
                      })}
                  </ul>
                </Card>
              ))}
            </div>
          )}
          <Card className="overflow-x-auto">
            <div className="flex flex-wrap items-center gap-2 border-b p-3">
              <Select
                className="h-8 w-auto"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setShown(PAGE);
                }}
                aria-label="Result"
              >
                <option value="">Every result</option>
                {Object.entries(LINK_STATUS).map(([k, s]) => (
                  <option key={k} value={k}>
                    {s.label}
                  </option>
                ))}
              </Select>
              <span className="text-xs text-[var(--color-muted-foreground)]">
                {formatInt(rows.length)} {rows.length === 1 ? "member" : "members"}
                {rows.length === 3000 ? " (the first 3,000; filter by result to see the rest)" : ""}
              </span>
            </div>
            {rows.length === 0 ? (
              <Empty title="No members match." />
            ) : (
              <Table>
                <THead>
                  <tr>
                    <TH>Member</TH>
                    <TH>Result</TH>
                    <TH>Card Keap charged</TH>
                    <TH>What happened</TH>
                    <TH>Checked (MT)</TH>
                  </tr>
                </THead>
                <tbody>
                  {visible.map((x) => {
                    const s = LINK_STATUS[x.status] ?? { label: x.status, variant: "muted" as const };
                    return (
                      <TR key={x.contact_id}>
                        <TD className="max-w-[14rem] truncate">
                          <Link to="/contacts/$contactId" params={{ contactId: String(x.contact_id) }} search={{ view: "billing" }} className="hover:underline">
                            {name(x.contact_id)}
                          </Link>
                        </TD>
                        <TD>
                          <span className="flex flex-wrap items-center gap-1">
                            <Badge variant={s.variant}>{s.label}</Badge>
                            {x.dry_run && <Badge variant="outline">dry run</Badge>}
                          </span>
                        </TD>
                        <TD className="whitespace-nowrap text-xs">{x.expected_last4 ? `${x.expected_brand ?? "Card"} ending ${x.expected_last4}` : "—"}</TD>
                        <TD className="max-w-[28rem] text-xs">
                          {x.detail ?? "—"}
                          {x.attempts > 1 && <span className="text-[var(--color-muted-foreground)]"> ({x.attempts} tries)</span>}
                        </TD>
                        <TD className="num whitespace-nowrap text-xs">{formatDateTime(x.checked_at)}</TD>
                      </TR>
                    );
                  })}
                </tbody>
              </Table>
            )}
            {rows.length > shown && (
              <div className="border-t p-2 text-center">
                <Button size="sm" variant="outline" onClick={() => setShown((n) => n + PAGE)}>
                  Show more
                </Button>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
